/**
 * WHAT SOMEBODY DID JUST BEFORE THEY REPORTED A FAULT.
 *
 * "It did not work" is the report we actually get, and on its own it is close
 * to useless: the one thing needed to reproduce a fault is the sequence that
 * led to it, and that is exactly what somebody cannot remember by the time they
 * have found the button to tell you. So it is kept for them.
 *
 * A RING OF THE LAST FORTY, and no more. It exists for the minute before the
 * report, not for a history — and a buffer that grows is a memory leak in a
 * page people leave open all day.
 *
 * ⚠️ "NOTHING IS SENT UNTIL THEY PRESS SEND" WAS TRUE UNTIL 30 AUGUST 2026, and
 * is corrected here rather than left to be discovered. #265 sends a FAULT on
 * its own — the message, the build, the reference already shown on screen, and
 * this trail — because an error nobody chooses to report is an error nobody
 * ever hears about, and that cost a day the day it was built. See
 * src/reporting.ts, and the broken screen, which now says so to the person it
 * happened to.
 *
 * WHAT STILL WAITS FOR SEND: everything else. An ordinary report, with whatever
 * somebody chose to write in it, goes only when they press the button. The
 * automatic path carries a fault and its trail and nothing a person typed.
 *
 * WHAT IT DELIBERATELY DOES NOT KEEP:
 *   · what they typed. A trail with keystrokes in it is a keylogger, whatever
 *     it is called, and the field somebody types a password into looks exactly
 *     like the field they type a title into.
 *   · issue titles, comment text, board names. A report crosses from their
 *     company into ours — see supabase/functions/feedback — so anything kept
 *     here crosses with it. What was pressed is enough to reproduce a fault;
 *     what it was called is theirs.
 */
export interface TrailEntry {
  at: number;
  /** 'did' — something they pressed. 'saw' — a screen. 'broke' — an error. */
  kind: 'did' | 'saw' | 'broke';
  what: string;
}

const KEEP = 40;
const ring: TrailEntry[] = [];

/**
 * THE FAILURES OUTLIVE THE PAGE THEY HAPPENED ON.
 *
 * WHAT WENT WRONG WITHOUT THIS. #254 puts a reference on the broken screen so
 * that what somebody quotes can be matched to what was recorded. The witness
 * checked whether it could be, and it could not: the recording lived in the
 * ring above, the ring lives in the page, and BOTH ways off that screen —
 * reloading, and starting again — are navigations that throw the page away. So
 * a customer rang up with `UJF-9E4` and there was nothing left anywhere holding
 * that code. The screen kept a promise it could not keep.
 *
 * WHAT IS KEPT, AND WHY ONLY THIS. Failures only. The rest of the trail is what
 * somebody pressed, which is worth having in a report written now and worth
 * nothing tomorrow — and a page people leave open all day should not be filling
 * storage with it. Failures are the part somebody quotes a reference from, days
 * after the fact.
 *
 * IT IS STILL NOT A SERVER RECORD, and this must not be mistaken for one. It
 * makes the reference survive long enough to reach a report the person chooses
 * to send. Being able to look a reference up without them is #265.
 *
 * NOTHING NEW CROSSES A BOUNDARY. These are the same entries a report already
 * carries, held in the same browser, for a day. See the note at the top for
 * what a trail refuses to contain in the first place.
 */
const FAULTS = 'flightdeck-faults';
const A_DAY = 24 * 60 * 60 * 1000;
const KEEP_FAULTS = 5;

const readFaults = (): TrailEntry[] => {
  try {
    const raw = localStorage.getItem(FAULTS);
    if (!raw) return [];
    const now = Date.now();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (e): e is TrailEntry =>
          !!e &&
          typeof (e as TrailEntry).at === 'number' &&
          typeof (e as TrailEntry).what === 'string' &&
          now - (e as TrailEntry).at < A_DAY
      )
      .map((e) => ({ at: e.at, kind: 'broke' as const, what: e.what }))
      .slice(-KEEP_FAULTS);
  } catch {
    /* Unreadable, full, or refused outright in private browsing. Not a fault. */
    return [];
  }
};

const keepFault = (entry: TrailEntry): void => {
  try {
    localStorage.setItem(FAULTS, JSON.stringify([...readFaults(), entry].slice(-KEEP_FAULTS)));
  } catch {
    /* nothing to do */
  }
};

/** Cap the words, because a stray label should not become a paragraph. */
const short = (s: string) => s.replace(/\s+/g, ' ').trim().slice(0, 90);

/**
 * `stack` IS PASSED THROUGH AND NEVER KEPT — #265.
 *
 * The ring is shown to the person, in their own words, and a stack is neither.
 * But the three places that catch a fault are the only places that ever hold
 * the error object, and by the time anything else looks, the stack is gone. So
 * it is handed to the watchers as it goes past and stored nowhere.
 */
export function remember(kind: TrailEntry['kind'], what: string, stack?: string): void {
  const text = short(what);
  if (!text) return;
  const last = ring[ring.length - 1];
  /*
   * The same thing twice running is counted, not repeated. Somebody pressing a
   * dead button six times is the most useful line in the report, and six
   * identical lines would push everything else out of a ring of forty.
   */
  if (last && last.kind === kind && last.what.replace(/ ×\d+$/, '') === text) {
    const times = Number(last.what.match(/ ×(\d+)$/)?.[1] ?? 1) + 1;
    last.what = `${text} ×${times}`;
    last.at = Date.now();
    /*
     * AND THE WATCHERS STILL HEAR IT, which is not obvious and matters most in
     * the one case this was built for. A render loop throws the SAME error
     * continuously, so every occurrence after the first lands here — and if
     * this returned quietly, a fault seen ten thousand times would be reported
     * as seen once. The reporter counts what it is told; it cannot count what
     * it is not told.
     */
    if (kind === 'broke') tell(last, stack);
    return;
  }
  const entry: TrailEntry = { at: Date.now(), kind, what: text };
  ring.push(entry);
  if (ring.length > KEEP) ring.shift();
  if (kind === 'broke') {
    keepFault(entry);
    tell(entry, stack);
  }
}

/**
 * ONE PLACE WHERE EVERY FAULT IS ALREADY KNOWN — #265. Window errors, unhandled
 * rejections, a render that threw and the handful of writes that report their
 * own failure all arrive at `remember` and nowhere else. So the reporting hook
 * goes here rather than being wired to four listeners that can drift apart, and
 * a fifth added tomorrow is covered by having used `remember`.
 *
 * NEVER THROWS BACK INTO THE APP. A collector that fails while recording a
 * fault must not become the fault, and must not turn one broken screen into a
 * loop of them.
 */
function tell(entry: TrailEntry, stack?: string): void {
  for (const watcher of watchers) {
    try {
      watcher(entry, stack);
    } catch {
      /* deliberate */
    }
  }
}

/** Called with every fault, as it happens. See src/reporting.ts. */
const watchers: ((e: TrailEntry, stack?: string) => void)[] = [];
export function whenSomethingBreaks(fn: (e: TrailEntry, stack?: string) => void): void {
  watchers.push(fn);
}

/** Only the failures, which are the part worth putting at the top. */
export function problems(): TrailEntry[] {
  return ring.filter((e) => e.kind === 'broke');
}

/** Oldest first, as a person would tell it. Seconds ago, not clock times. */
export function trail(): string {
  if (ring.length === 0) return '';
  const now = Date.now();
  return ring
    .map((e) => {
      const ago = Math.round((now - e.at) / 1000);
      const when = ago < 60 ? `${ago}s ago` : `${Math.round(ago / 60)}m ago`;
      const mark = e.kind === 'broke' ? 'ERROR' : e.kind === 'saw' ? 'went to' : 'pressed';
      return `  ${when.padStart(7)}  ${mark} ${e.what}`;
    })
    .join('\n');
}

/** For tests, and for nothing else — a page never wants to forget. */
export function forgetEverything(): void {
  ring.length = 0;
  try {
    localStorage.removeItem(FAULTS);
  } catch {
    /* nothing to do */
  }
}

/**
 * START LISTENING. Called once, from the app's entry point.
 *
 * DONE WITH LISTENERS RATHER THAN BY CALLING `remember` FROM EVERY BUTTON. Two
 * reasons, and the second is the one that decided it: three hundred call sites
 * is three hundred chances to forget one, and the buttons nobody remembered to
 * instrument would be the broken ones — a trail that goes quiet exactly where
 * the fault is. This way a button added next year is recorded without anybody
 * thinking about it.
 */
export function watchForTheTrail(): void {
  /*
   * WHAT BROKE BEFORE THIS PAGE EXISTED, put back first so it reads oldest to
   * newest like everything after it. This is what makes the reference on the
   * broken screen still mean something once somebody has reloaded past it.
   */
  for (const fault of readFaults()) {
    if (!ring.some((e) => e.at === fault.at && e.what === fault.what)) ring.push(fault);
  }
  ring.sort((a, b) => a.at - b.at);

  /*
   * THE LABEL, NOT THE VALUE. `aria-label` first because it is written for
   * exactly this — saying what a control does — then the visible text. Inputs
   * are named and never read: see the note at the top about keystrokes.
   */
  document.addEventListener(
    'click',
    (e) => {
      const el = (e.target as HTMLElement | null)?.closest(
        'button, a, [role="button"], [role="menuitem"], [role="radio"], [role="tab"]'
      );
      if (!el) return;
      const said =
        el.getAttribute('aria-label') ||
        (el as HTMLElement).innerText ||
        el.getAttribute('title') ||
        el.tagName.toLowerCase();
      remember('did', said);
    },
    // Captured, so a handler that stops the event still leaves a trace. A
    // button whose press goes nowhere is the most likely thing to be reported.
    true
  );

  window.addEventListener('error', (e) => {
    remember(
      'broke',
      `${e.message} (${String(e.filename).split('/').pop()}:${e.lineno})`,
      (e.error as { stack?: string } | null)?.stack
    );
  });
  window.addEventListener('unhandledrejection', (e) => {
    const why = (e.reason as { message?: string })?.message ?? String(e.reason);
    remember('broke', `unhandled: ${why}`, (e.reason as { stack?: string } | null)?.stack);
  });
}
